import React, { createContext, useContext, useEffect, useState } from 'react';
import type { Nullable } from '@gov-nx/core/types';
import { first } from '@gov-nx/core/types';
import { useMyFilesStorage } from '@gov-nx/module/page';
import { useDataBoxStore, useDataBoxStoreLists } from '@gov-nx/store';
import { useBoolean } from '@gov-nx/utils/common';
import { useDataBoxSettings } from '../hooks/useDataBoxSettings';
import { useDataBoxUnreadMessages } from '../hooks/useDataBoxUnreadMessagesCounts';
import type { DataBoxFolderType } from './DataBoxes.types';
import type { DataBoxesContextType } from './DataBoxesContext.types';

export const DataBoxesContext =
	createContext<Nullable<DataBoxesContextType>>(null);

interface DataBoxesProviderProps {
	dataBoxId: string;
	folderType: DataBoxFolderType;
	children: React.ReactNode;
}

export function DataBoxesProvider({
	dataBoxId: initedDataBoxId,
	folderType: initedFolderType,
	children,
}: DataBoxesProviderProps) {
	const [dataBoxId, setDataBoxId] =
		useState<DataBoxesContextType['dataBoxId']>(initedDataBoxId);
	const [folderType, setFolderType] =
		useState<DataBoxesContextType['folderType']>(initedFolderType);

	const {
		connectedAndDisconnectedDataBoxes,
		connectedDataBoxes,
		connectedDataBoxesIds,
	} = useDataBoxStoreLists();
	const lastOpened = useDataBoxStore('lastOpenedDatabox');
	const isDataBoxesLoaded = useDataBoxStore('isLoaded');
	const firstDataBox = first(connectedAndDisconnectedDataBoxes);

	const storage = useMyFilesStorage();

	const isSupportModalOpen = useBoolean();
	const isCancelPromptOpen = useBoolean();

	const isDataBoxConnected = (dataBoxId: string) =>
		connectedDataBoxesIds.includes(dataBoxId);

	const [creditModalDataBoxId, setCreditModalDataBoxId] =
		useState<Nullable<string>>(null);

	const closeCreditModal = () => {
		setCreditModalDataBoxId(null);
	};

	const handleCreditModalOpen = (dataBoxId: string) => {
		setCreditModalDataBoxId(dataBoxId);
	};

	const [mobileMenuDataBox, setMobileMenuDataBox] =
		useState<Nullable<string>>(null);

	const disableNewMessageButton = !connectedDataBoxes.length;

	useEffect(() => {
		setDataBoxId(initedDataBoxId);
	}, [initedDataBoxId]);

	useEffect(() => {
		setFolderType(initedFolderType);
	}, [initedFolderType]);

	const { automaticArchiveEnabled, notificationsEnabled } = useDataBoxSettings({
		dataBoxId: initedDataBoxId,
	});

	const { getUnreadMessagesCounts } = useDataBoxUnreadMessages();

	return (
		<DataBoxesContext.Provider
			value={{
				setDataBoxId,
				setFolderType,
				dataBoxes: connectedAndDisconnectedDataBoxes,
				firstDataBox,
				isDataBoxesLoaded: isDataBoxesLoaded,
				dataBoxId,
				folderType,
				storage,
				creditModalDataBoxId,
				closeCreditModal,
				handleCreditModalOpen,
				isCancelPromptOpen,
				isSupportModalOpen,
				mobileMenuDataBox,
				setMobileMenuDataBox,
				disableNewMessageButton,
				automaticArchiveEnabled,
				notificationsEnabled,
				lastOpened,
				isDataBoxConnected,
				getUnreadMessagesCounts,
			}}>
			{children}
		</DataBoxesContext.Provider>
	);
}

export const useDataBoxesContext = (): DataBoxesContextType =>
	useContext(DataBoxesContext) as DataBoxesContextType;
